"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Clock, BookOpen, FileCheck, ArrowRight, Image as ImageIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { CourseCatalogItem } from "@/types";

function formatDuration(minutes: number) {
  if (minutes <= 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `${hours} hr${hours > 1 ? "s" : ""}`
    : `${hours} hr${hours > 1 ? "s" : ""} ${remainingMinutes} min`;
}

interface CourseCardProps {
  course: CourseCatalogItem;
}

export function CourseCard({ course }: CourseCardProps) {
  const coverImage = course.heroImageUrl || course.thumbnail;

  return (
    <motion.article
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/20 shadow-lg transition-shadow hover:shadow-xl"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 opacity-0 transition-opacity group-hover:opacity-100" />

      <div className="relative">
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-2xl bg-secondary/40 isolate transform translate-z-0">
          {coverImage ? (
            <Image
              src={coverImage}
              alt={`${course.title} thumbnail`}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105 will-change-transform"
              sizes="(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw"
              unoptimized
            />
          ) : (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <ImageIcon className="h-5 w-5" />
              Thumbnail coming soon
            </div>
          )}
          <div className="absolute inset-x-0 -bottom-[1px] -top-px bg-gradient-to-t from-background/90 via-background/40 to-transparent" />
        </div>
      </div>

      <div className="relative flex-1 space-y-4 p-6 pb-0">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide">
          <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-primary">
            {course.category}
          </span>
          <span className="rounded-full border border-border/50 bg-secondary/50 px-3 py-1 text-muted-foreground">
            {course.level}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2 transition-transform group-hover:scale-110">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="line-clamp-2 text-xl font-semibold leading-tight text-foreground">
                {course.title}
              </h3>
              <p className="line-clamp-3 text-sm text-muted-foreground">{course.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative space-y-4 p-6 pt-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-lg bg-secondary/80 px-3 py-1.5 font-medium text-secondary-foreground">
            <Clock className="h-3 w-3" />
            {formatDuration(course.estimatedMinutes)}
          </span>
          <span className="inline-flex items-center gap-1 rounded-lg bg-secondary/80 px-3 py-1.5 font-medium text-secondary-foreground">
            <BookOpen className="h-3 w-3" />
            {course.lessonCount} lessons
          </span>
          <span className="inline-flex items-center gap-1 rounded-lg bg-secondary/80 px-3 py-1.5 font-medium text-secondary-foreground">
            <FileCheck className="h-3 w-3" />
            {course.assessmentCount}
          </span>
        </div>

        <div className="rounded-lg border border-border/50 bg-secondary/30 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground/70">
            Learning outcomes
          </p>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            {course.featuredOutcomes.slice(0, 3).map((outcome) => (
              <li key={outcome} className="flex items-start gap-2">
                <span className="mt-0.5 h-1 w-1 shrink-0 rounded-full bg-primary" />
                <span className="line-clamp-1">{outcome}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
            {course.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-border/50 bg-background/50 px-2 py-0.5"
              >
                #{tag}
              </span>
            ))}
          </div>
          <Button asChild size="sm" className="group/btn">
            <Link href={`/courses/${course.slug}`} className="inline-flex items-center gap-2">
              View course
              <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </motion.article>
  );
}
